/**
 * StatusGjennomgang - Interaktiv gjennomgang av saksstatus
 *
 * Går gjennom alle saker én om gangen og lar brukeren bekrefte eller oppdatere status
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './StatusGjennomgang.css';

export default function StatusGjennomgang({ navigerTil }) {
  const [saker, setSaker] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nyStatus, setNyStatus] = useState('');
  const [nyFase, setNyFase] = useState('');
  const [notat, setNotat] = useState('');
  const [endringer, setEndringer] = useState([]);
  const [ferdig, setFerdig] = useState(false);
  const [filter, setFilter] = useState('alle'); // 'alle', 'aktiv', 'venter', 'avsluttet'

  useEffect(() => {
    lastSaker();
  }, []);

  const lastSaker = async () => {
    try {
      setLoading(true);
      const data = await crmApi.getSaker();
      let sakArray = Array.isArray(data) ? data : (data.saker || []);

      // Sorter: etter saksfase
      sakArray = sakArray.sort((a, b) => {
        const statusOrder = {
          'forhåndsvurdering': 0,
          'oppdragsbekreftelse': 1,
          'innhenting_av_opplysninger': 2,
          'juridisk_vurdering': 3,
          'spesifisert_krav': 4,
          'søksmålsvarsel': 5,
          'søksmål': 6,
          'meklingsmøte': 7,
          'rettsmøte': 8,
          'aktiv': 10,
          'venter': 11,
          'avsluttet': 12,
          'arkivert': 13
        };
        return (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99);
      });

      setSaker(sakArray);
      if (sakArray.length > 0) {
        setNyStatus(sakArray[0].status);
        setNyFase(sakArray[0].fase || '');
      }
    } catch (error) {
      console.error('Feil ved lasting av saker:', error);
      setSaker([]);
    } finally {
      setLoading(false);
    }
  };

  const currentSak = saker[currentIndex];

  const filtrerSaker = () => {
    if (filter === 'alle') return saker;
    return saker.filter(s => s.status === filter);
  };

  const filtrerteIndecer = () => {
    if (filter === 'alle') return Array.from({ length: saker.length }, (_, i) => i);
    return saker.map((s, i) => ({ sak: s, index: i }))
                .filter(({ sak }) => sak.status === filter)
                .map(({ index }) => index);
  };

  const handleBekreft = async () => {
    // Sak er OK, gå til neste
    await handleNeste(false);
  };

  const handleOppdater = async () => {
    if (!currentSak) return;

    try {
      setSaving(true);

      const oppdatertData = {
        status: nyStatus,
        fase: nyFase || null
      };

      await crmApi.updateSak(currentSak.id, oppdatertData);

      // Logg endringen
      const endring = {
        saksnummer: currentSak.saksnummer,
        klient: currentSak.klient_navn,
        gammelStatus: currentSak.status,
        gammelFase: currentSak.fase,
        nyStatus: nyStatus,
        nyFase: nyFase,
        notat: notat,
        tidspunkt: new Date().toISOString()
      };
      setEndringer(prev => [...prev, endring]);

      // Oppdater lokal state
      const oppdaterteSaker = [...saker];
      oppdaterteSaker[currentIndex] = {
        ...currentSak,
        status: nyStatus,
        fase: nyFase
      };
      setSaker(oppdaterteSaker);

      // Reset og gå til neste
      setNotat('');
      await handleNeste(true);

    } catch (error) {
      console.error('Feil ved oppdatering av sak:', error);
      alert('Kunne ikke oppdatere sak: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleNeste = async (erOppdatering) => {
    const indekser = filtrerteIndecer();
    const currentPosisjon = indekser.indexOf(currentIndex);

    if (currentPosisjon < indekser.length - 1) {
      const nesteIndex = indekser[currentPosisjon + 1];
      setCurrentIndex(nesteIndex);
      setNyStatus(saker[nesteIndex].status);
      setNyFase(saker[nesteIndex].fase || '');
      setNotat('');
    } else {
      // Ferdig med alle saker
      setFerdig(true);
    }
  };

  const handleForrige = () => {
    const indekser = filtrerteIndecer();
    const currentPosisjon = indekser.indexOf(currentIndex);

    if (currentPosisjon > 0) {
      const forrigeIndex = indekser[currentPosisjon - 1];
      setCurrentIndex(forrigeIndex);
      setNyStatus(saker[forrigeIndex].status);
      setNyFase(saker[forrigeIndex].fase || '');
      setNotat('');
    }
  };

  const handleHoppTilSak = (index) => {
    setCurrentIndex(index);
    setNyStatus(saker[index].status);
    setNyFase(saker[index].fase || '');
    setNotat('');
    setFerdig(false);
  };

  const handleAvbryt = () => {
    if (navigerTil) {
      navigerTil('saker');
    } else {
      window.location.href = '/saker';
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      'aktiv': 'status-badge-aktiv',
      'venter': 'status-badge-venter',
      'avsluttet': 'status-badge-avsluttet',
      'arkivert': 'status-badge-arkivert'
    };
    return classes[status] || '';
  };

  const getFaseBadgeClass = (fase) => {
    if (!fase) return '';
    const classes = {
      'forhåndsvurdering': 'status-badge-forhandsvurdering',
      'oppdragsbekreftelse': 'status-badge-oppdragsbekreftelse',
      'innhenting_av_opplysninger': 'status-badge-innhenting',
      'juridisk_vurdering': 'status-badge-juridisk',
      'spesifisert_krav': 'status-badge-krav',
      'søksmålsvarsel': 'status-badge-varsel',
      'søksmål': 'status-badge-soksmål',
      'meklingsmøte': 'status-badge-mekling',
      'rettsmøte': 'status-badge-rettsmote'
    };
    return classes[fase] || '';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'aktiv': 'Aktiv',
      'venter': 'Venter',
      'avsluttet': 'Avsluttet',
      'arkivert': 'Arkivert'
    };
    return labels[status] || status;
  };

  const getFaseLabel = (fase) => {
    if (!fase) return '';
    const labels = {
      'forhåndsvurdering': 'Forhåndsvurdering',
      'oppdragsbekreftelse': 'Oppdragsbekreftelse',
      'innhenting_av_opplysninger': 'Innhenting av opplysninger',
      'juridisk_vurdering': 'Juridisk vurdering',
      'spesifisert_krav': 'Spesifisert krav',
      'søksmålsvarsel': 'Søksmålsvarsel',
      'søksmål': 'Søksmål',
      'meklingsmøte': 'Meklingsmøte',
      'rettsmøte': 'Rettsmøte'
    };
    return labels[fase] || fase;
  };

  if (loading) {
    return (
      <div className="statusgjennomgang-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Laster saker...</p>
        </div>
      </div>
    );
  }

  if (saker.length === 0) {
    return (
      <div className="statusgjennomgang-container">
        <div className="empty-state">
          <h2>Ingen saker funnet</h2>
          <p>Det finnes ingen saker å gjennomgå.</p>
          <button onClick={handleAvbryt} className="btn-primary">
            Tilbake til saker
          </button>
        </div>
      </div>
    );
  }

  if (ferdig) {
    return (
      <div className="statusgjennomgang-container">
        <div className="completion-view">
          <div className="completion-header">
            <h1>✅ Statusgjennomgang fullført!</h1>
            <p>Du har gått gjennom alle {filtrerteIndecer().length} saker.</p>
          </div>

          {endringer.length > 0 && (
            <div className="endringer-summary">
              <h2>Endringer ({endringer.length})</h2>
              <div className="endringer-liste">
                {endringer.map((endring, i) => (
                  <div key={i} className="endring-item">
                    <div className="endring-header">
                      <strong>{endring.saksnummer}</strong>
                      <span className="endring-klient">{endring.klient}</span>
                    </div>
                    <div className="endring-status">
                      <div style={{ marginBottom: '8px' }}>
                        <strong>Status:</strong>{' '}
                        <span className={`status-badge ${getStatusBadgeClass(endring.gammelStatus)}`}>
                          {getStatusLabel(endring.gammelStatus)}
                        </span>
                        <span className="status-arrow">→</span>
                        <span className={`status-badge ${getStatusBadgeClass(endring.nyStatus)}`}>
                          {getStatusLabel(endring.nyStatus)}
                        </span>
                      </div>
                      {(endring.gammelFase || endring.nyFase) && (
                        <div>
                          <strong>Fase:</strong>{' '}
                          <span className={`status-badge ${getFaseBadgeClass(endring.gammelFase)}`}>
                            {getFaseLabel(endring.gammelFase) || 'Ingen'}
                          </span>
                          <span className="status-arrow">→</span>
                          <span className={`status-badge ${getFaseBadgeClass(endring.nyFase)}`}>
                            {getFaseLabel(endring.nyFase) || 'Ingen'}
                          </span>
                        </div>
                      )}
                    </div>
                    {endring.notat && (
                      <div className="endring-notat">
                        <em>Notat: {endring.notat}</em>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {endringer.length === 0 && (
            <div className="ingen-endringer">
              <p>Ingen endringer ble gjort. Alle saker hadde riktig status.</p>
            </div>
          )}

          <div className="completion-actions">
            <button onClick={() => {
              setFerdig(false);
              setCurrentIndex(0);
              setEndringer([]);
              lastSaker();
            }} className="btn-secondary">
              Gjennomgå på nytt
            </button>
            <button onClick={handleAvbryt} className="btn-primary">
              Tilbake til saker
            </button>
          </div>
        </div>
      </div>
    );
  }

  const indekser = filtrerteIndecer();
  const currentPosisjon = indekser.indexOf(currentIndex) + 1;
  const totalt = indekser.length;
  const progress = (currentPosisjon / totalt) * 100;

  return (
    <div className="statusgjennomgang-container">
      <div className="statusgjennomgang-header">
        <h1>Statusgjennomgang</h1>
        <button onClick={handleAvbryt} className="btn-close">
          ✕ Avbryt
        </button>
      </div>

      <div className="progress-section">
        <div className="progress-info">
          <span>Sak {currentPosisjon} av {totalt}</span>
          <span className="endringer-count">
            {endringer.length} {endringer.length === 1 ? 'endring' : 'endringer'}
          </span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }}></div>
        </div>
      </div>

      <div className="filter-section">
        <label>Vis saker:</label>
        <select value={filter} onChange={(e) => {
          setFilter(e.target.value);
          const nyeIndekser = e.target.value === 'alle'
            ? Array.from({ length: saker.length }, (_, i) => i)
            : saker.map((s, i) => ({ sak: s, index: i }))
                   .filter(({ sak }) => sak.status === e.target.value)
                   .map(({ index }) => index);
          if (nyeIndekser.length > 0) {
            setCurrentIndex(nyeIndekser[0]);
            setNyStatus(saker[nyeIndekser[0]].status);
          }
        }}>
          <option value="alle">Alle saker</option>
          <option value="aktiv">Kun aktive</option>
          <option value="venter">Kun venter</option>
          <option value="avsluttet">Kun avsluttede</option>
        </select>
      </div>

      {currentSak && (
        <div className="sak-card">
          <div className="sak-header">
            <div>
              <h2>{currentSak.saksnummer}</h2>
              <p className="sak-klient">{currentSak.klient_navn}</p>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexDirection: 'column', alignItems: 'flex-end' }}>
              <span className={`status-badge ${getStatusBadgeClass(currentSak.status)}`}>
                {getStatusLabel(currentSak.status)}
              </span>
              {currentSak.fase && (
                <span className={`status-badge ${getFaseBadgeClass(currentSak.fase)}`}>
                  {getFaseLabel(currentSak.fase)}
                </span>
              )}
            </div>
          </div>

          <div className="sak-info">
            {currentSak.sakstype && (
              <div className="info-row">
                <span className="info-label">Type:</span>
                <span>{currentSak.sakstype}</span>
              </div>
            )}
            {currentSak.beskrivelse && (
              <div className="info-row">
                <span className="info-label">Beskrivelse:</span>
                <span>{currentSak.beskrivelse}</span>
              </div>
            )}
            {currentSak.opprettet_dato && (
              <div className="info-row">
                <span className="info-label">Opprettet:</span>
                <span>{new Date(currentSak.opprettet_dato).toLocaleDateString('nb-NO')}</span>
              </div>
            )}
            {currentSak.sist_endret && (
              <div className="info-row">
                <span className="info-label">Sist endret:</span>
                <span>{new Date(currentSak.sist_endret).toLocaleDateString('nb-NO')}</span>
              </div>
            )}
          </div>

          <div className="status-question">
            <h3>Er status og fase korrekt?</h3>
            <p>
              Nåværende status: <strong>{getStatusLabel(currentSak.status)}</strong>
              {currentSak.fase && <> • Fase: <strong>{getFaseLabel(currentSak.fase)}</strong></>}
            </p>
          </div>

          <div className="status-valg">
            <label>Status:</label>
            <select
              value={nyStatus}
              onChange={(e) => setNyStatus(e.target.value)}
              className="status-select"
            >
              <option value="aktiv">Aktiv</option>
              <option value="venter">Venter</option>
              <option value="avsluttet">Avsluttet</option>
              <option value="arkivert">Arkivert</option>
            </select>
          </div>

          {nyStatus === 'aktiv' && (
            <div className="status-valg">
              <label>Fase (kun for aktive saker):</label>
              <select
                value={nyFase}
                onChange={(e) => setNyFase(e.target.value)}
                className="status-select"
              >
                <option value="">Ingen spesifikk fase</option>
                <option value="forhåndsvurdering">Forhåndsvurdering</option>
                <option value="oppdragsbekreftelse">Oppdragsbekreftelse</option>
                <option value="innhenting_av_opplysninger">Innhenting av opplysninger</option>
                <option value="juridisk_vurdering">Juridisk vurdering</option>
                <option value="spesifisert_krav">Spesifisert krav</option>
                <option value="søksmålsvarsel">Søksmålsvarsel</option>
                <option value="søksmål">Søksmål</option>
                <option value="meklingsmøte">Meklingsmøte</option>
                <option value="rettsmøte">Rettsmøte</option>
              </select>
            </div>
          )}

          {(nyStatus !== currentSak.status || nyFase !== (currentSak.fase || '')) && (
            <div className="notat-section">
              <label>Notat (valgfritt):</label>
              <textarea
                value={notat}
                onChange={(e) => setNotat(e.target.value)}
                placeholder="Hvorfor endres status? (valgfritt)"
                rows={3}
                className="notat-input"
              />
            </div>
          )}

          <div className="actions">
            <button
              onClick={handleForrige}
              disabled={currentPosisjon === 1}
              className="btn-secondary"
            >
              ← Forrige
            </button>

            {(nyStatus === currentSak.status && nyFase === (currentSak.fase || '')) ? (
              <button
                onClick={handleBekreft}
                className="btn-success"
              >
                ✓ Status/Fase OK - Neste
              </button>
            ) : (
              <button
                onClick={handleOppdater}
                disabled={saving}
                className="btn-primary"
              >
                {saving ? 'Oppdaterer...' : 'Oppdater status/fase'}
              </button>
            )}

            <button
              onClick={() => handleNeste(false)}
              disabled={currentPosisjon === totalt}
              className="btn-secondary"
            >
              Hopp over →
            </button>
          </div>
        </div>
      )}

      {endringer.length > 0 && (
        <div className="endringer-preview">
          <h3>Endringer så langt ({endringer.length})</h3>
          <div className="endringer-liste-mini">
            {endringer.slice(-3).reverse().map((endring, i) => (
              <div key={i} className="endring-mini">
                <strong>{endring.saksnummer}:</strong>
                <span className={`status-badge-mini ${getStatusBadgeClass(endring.gammelStatus)}`}>
                  {getStatusLabel(endring.gammelStatus)}
                </span>
                →
                <span className={`status-badge-mini ${getStatusBadgeClass(endring.nyStatus)}`}>
                  {getStatusLabel(endring.nyStatus)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
